import React from 'react';
import type { PlaybookEntity } from '../types/playbook.types';

interface PlaybookActionsTabProps {
  playbook: PlaybookEntity;
}

const ACTION_COLORS: Record<string, string> = {
  create_task: 'bg-lex-accent-soft text-lex-accent',
  create_subtask: 'bg-lex-accent-soft text-lex-accent',
  send_notification: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  calculate_deadline: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  assign_to_role: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  webhook: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  ai_generate: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const PlaybookActionsTab: React.FC<PlaybookActionsTabProps> = ({ playbook }) => {
  const transitions = playbook.transitions || [];
  const withActions = transitions.filter((t) => (t.actions?.length || 0) > 0);

  if (withActions.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
        No actions configured for any transition.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {withActions.map((t) => (
        <div key={t.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">
            {t.name} <span className="text-gray-400 font-normal">({t.slug})</span>
          </h4>
          <div className="space-y-1">
            {t.actions?.sort((a, b) => a.execution_order - b.execution_order).map((a) => (
              <div key={a.id} className="flex items-start gap-2 py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-800/50">
                <span className="text-[10px] text-gray-400 w-4 text-right shrink-0">#{a.execution_order}</span>
                <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium shrink-0 ${
                  ACTION_COLORS[a.action_type] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {a.action_type}
                </span>
                {a.is_async && (
                  <span className="text-[9px] text-amber-600 dark:text-amber-400 font-medium">ASYNC</span>
                )}
                <span className={`text-[9px] font-medium ${
                  a.error_handling === 'block' ? 'text-rose-500' :
                  a.error_handling === 'rollback' ? 'text-amber-500' :
                  'text-gray-400'
                }`}>
                  {a.error_handling}
                </span>
                {Object.keys(a.configuration).length > 0 && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate">
                    {JSON.stringify(a.configuration).slice(0, 100)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlaybookActionsTab;
